import type { JSX } from "react";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { Screen } from "../components/ui/editorial/Screen.js";
import { Topline } from "../components/ui/editorial/Topline.js";
import { Portrait } from "../components/ui/editorial/Portrait.js";

interface MatchData {
  id: string;
  name: string;
  possessive: string; // ваш / ваша
  companion: string; // спутник / спутница
  bio: string;
  username: string; // telegram handle, без @
  invitation: string; // личное приглашение на основе её анкеты
}

// Шаблоны подобраны редактором под каждую пару — цепляются за конкретную
// деталь из её портрета, чтобы сразу было о чём говорить.
const DEFAULT_MATCH: MatchData = {
  id: "04",
  name: "Аня",
  possessive: "ваша",
  companion: "спутница",
  bio: "Редактор, 28, Москва. Читает Улицкую, любит тихие вечера и долгие прогулки вдоль реки.",
  username: "anya_reads",
  invitation:
    "Аня, привет. Марк сказал — тихие вечера и Улицкая. У меня на столе как раз «Казус Кукоцкого». Какая у вас любимая прогулка — вдоль какой реки?",
};

const MATCHES: Record<string, MatchData> = {
  "04": DEFAULT_MATCH,
  "03": {
    id: "03",
    name: "Марина",
    possessive: "ваша",
    companion: "спутница",
    bio: "Режиссёр, 31, Москва. Снимает короткое кино, не любит вечеринок, ценит хорошее кафе на окраине.",
    username: "marina_shoots",
    invitation:
      "Марина, привет. Марк передал — вы снимаете короткий метр и не любите вечеринок. Совпадение: у меня целый список кафе на окраинах, куда всё никак не дойду. Есть ваше любимое?",
  },
  "02": {
    id: "02",
    name: "Лиза",
    possessive: "ваша",
    companion: "спутница",
    bio: "Архитектор, 29, Москва. Собирает фасады, ищет тёплые кофейни и уличные сады посреди двора.",
    username: "liza_builds",
    invitation:
      "Лиза, привет. Марк написал про вас — и теперь я по дороге на работу высматриваю старые фасады, которые раньше не замечал. Есть двор в городе, который стоит показать?",
  },
  "01": {
    id: "01",
    name: "Катя",
    possessive: "ваша",
    companion: "спутница",
    bio: "Поэт, 26, Москва. Её любимое время — сумерки. Любит чай, бумагу и длинные письма.",
    username: "katya_companion_demo",
    invitation:
      "Катя, привет. Марк передал ваш портрет — зацепился за строку про сумерки. Какие из них в Москве — ваши самые честные?",
  },
};

export function Match(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params.id ?? "";

  // MATCHES — демо-данные для /brainstorm превью (id 01–04). Когда юзер
  // попадает сюда по реальному UUID письма — подбора ещё нет, честный
  // empty state вместо фейковой Ани.
  const m = MATCHES[id];

  const [copied, setCopied] = useState(false);
  const template = m?.invitation ?? "";

  // лёгкий haptic при открытии, если Telegram WebApp
  useEffect(() => {
    if (!m) return;
    const tg = (
      window as {
        Telegram?: {
          WebApp?: { HapticFeedback?: { notificationOccurred?: (type: string) => void } };
        };
      }
    ).Telegram?.WebApp;
    tg?.HapticFeedback?.notificationOccurred?.("success");
  }, [m]);

  // Пока нет реального подбора (MVP) — показываем честный empty state вместо
  // фейковых демо-персонажей.
  if (!m) {
    return (
      <Screen tone="paper">
        <div className="px-[22px] pt-[48px]">
          <Topline left="COMPANION" right="представление" />
        </div>
        <div className="flex flex-1 flex-col items-center justify-center px-[22px] text-center">
          <h1 className="font-display font-light text-[32px] leading-[1.05] text-ink">
            <em className="italic text-velvet">Представление</em>
            <br />
            ещё готовится.
          </h1>
          <p className="mt-[16px] max-w-[320px] font-display italic font-light text-[15px] leading-[1.5] text-ink/65">
            Редактор подбирает вам собеседника. Когда всё сложится — придёт уведомление в Telegram и
            письмо окажется в редакции.
          </p>
        </div>
        <div className="px-[22px] pb-[24px]">
          <button
            type="button"
            onClick={() => {
              navigate("/home");
            }}
            className="editorial-meta text-ink/55 hover:text-ink transition-colors"
          >
            ← в редакцию
          </button>
        </div>
      </Screen>
    );
  }

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(template);
      setCopied(true);
      window.setTimeout(() => {
        setCopied(false);
      }, 1800);
    } catch {
      // silently swallow — демо без тостов
    }
  };

  const onOpenTelegram = () => {
    const url = `https://t.me/${m.username}`;
    const tg = (
      window as {
        Telegram?: {
          WebApp?: {
            openTelegramLink?: (u: string) => void;
            openLink?: (u: string) => void;
          };
        };
      }
    ).Telegram?.WebApp;
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(url);
    } else if (tg?.openLink) {
      tg.openLink(url);
    } else {
      window.open(url, "_blank", "noreferrer");
    }
  };

  return (
    <div
      data-screen
      className="relative flex min-h-[100dvh] w-full flex-col bg-paper"
      style={{ fontFamily: "Inter, system-ui, sans-serif" }}
    >
      {/* бумажный фон как «лист под окном в ночь» */}
      <div className="relative z-0 px-[22px] pt-[48px] opacity-60">
        <Topline left="COMPANION" right="сегодня" />
        <div className="mt-[60px] text-center font-display font-light text-[14px] italic text-ink/40">
          …во время чтения пришёл ответ от Марка.
        </div>
      </div>

      {/* затемнение поверх бумаги */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="absolute inset-0 z-10"
        style={{
          background:
            "radial-gradient(ellipse at 50% 45%, rgba(11,6,20,0.35), rgba(11,6,20,0.72) 70%)",
        }}
      />

      {/* модал — flex-центрирование чтобы не конфликтовало с framer-motion */}
      <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center px-[22px]">
        <motion.div
          initial={{ opacity: 0, y: 14, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 0.7,
            delay: 0.15,
            ease: [0.16, 1, 0.3, 1],
          }}
          className="pointer-events-auto relative w-full max-w-[400px] max-h-[92dvh] overflow-y-auto rounded-[6px] p-[26px]"
          style={{
            background: "linear-gradient(168deg, #1f1338 0%, #0a0512 82%)",
            color: "#eae4f3",
            boxShadow:
              "0 28px 60px -12px rgba(11,6,20,0.55), inset 0 0 0 1px rgba(200,168,255,0.10)",
          }}
        >
          {/* Лавандовый blob снизу-слева — единственный цветовой акт */}
          <div
            aria-hidden
            className="pointer-events-none absolute bottom-[-72px] left-[-56px] z-0 h-[180px] w-[180px] rounded-full"
            style={{
              background: "radial-gradient(circle, rgba(200,168,255,0.22), transparent 62%)",
              filter: "blur(12px)",
            }}
          />

          <div className="relative z-10">
            <div
              className="flex items-center gap-[10px] font-sans text-[11px] font-semibold uppercase"
              style={{ letterSpacing: "0.34em", color: "rgba(234,228,243,0.7)" }}
            >
              <span
                aria-hidden
                className="inline-block h-[8px] w-[8px] rounded-full"
                style={{
                  background: "#c23228",
                  boxShadow: "0 0 8px rgba(194,50,40,0.55)",
                }}
              />
              <span>Совпадение</span>
            </div>

            <h1
              className="mt-[14px] font-display font-light leading-[1] tracking-[-0.024em]"
              style={{ fontSize: "42px" }}
            >
              {m.name} —{" "}
              <em className="italic" style={{ color: "#c8a8ff" }}>
                {m.possessive}
              </em>
              <br />
              {m.companion} сегодня
            </h1>

            {/* Портрет: живое лицо рядом с именем. Seed — имя, чтобы каждая
                карточка выглядела по-своему (тёплая/холодная/графитовая). */}
            <div className="mt-[18px]">
              <Portrait seed={m.name} aspect="4 / 3" />
            </div>

            <div className="my-[18px] h-px" style={{ background: "rgba(234,228,243,0.16)" }} />

            <p
              className="font-display font-light text-[15px] leading-[1.55]"
              style={{ color: "rgba(234,228,243,0.78)" }}
            >
              {m.bio}
            </p>

            {/* @username — Telegram-контакт */}
            <div
              className="mt-[22px] flex items-baseline justify-between pb-[12px]"
              style={{ borderBottom: "1px solid rgba(234,228,243,0.14)" }}
            >
              <span
                className="font-sans text-[10px] font-semibold uppercase"
                style={{
                  letterSpacing: "0.32em",
                  color: "rgba(234,228,243,0.5)",
                }}
              >
                Telegram
              </span>
              <button
                type="button"
                onClick={onOpenTelegram}
                className="font-display italic text-[18px] transition-opacity hover:opacity-80"
                style={{ color: "#c8a8ff" }}
              >
                @{m.username}
              </button>
            </div>

            {/* Шаблон приглашения */}
            <div className="mt-[14px]">
              <div className="flex items-baseline justify-between">
                <span
                  className="font-sans text-[10px] font-semibold uppercase"
                  style={{
                    letterSpacing: "0.32em",
                    color: "rgba(234,228,243,0.5)",
                  }}
                >
                  Приглашение · шаблон
                </span>
                <button
                  type="button"
                  onClick={onCopy}
                  className="font-sans text-[10px] font-semibold uppercase transition-opacity hover:opacity-80"
                  style={{
                    letterSpacing: "0.28em",
                    color: copied ? "#c8a8ff" : "rgba(234,228,243,0.65)",
                  }}
                >
                  {copied ? "скопировано ✓" : "скопировать"}
                </button>
              </div>
              <blockquote
                className="mt-[8px] pl-[12px] font-display italic font-light text-[14px] leading-[1.5]"
                style={{
                  borderLeft: "2px solid #c8a8ff",
                  color: "rgba(234,228,243,0.78)",
                }}
              >
                «{template}»
              </blockquote>
            </div>

            {/* Primary CTA — text-link, полная ширина, одна киноварная стрелка */}
            <button
              type="button"
              onClick={onOpenTelegram}
              className="mt-[22px] flex w-full items-baseline justify-between pt-[14px] font-display italic font-light text-[22px] tracking-[-0.01em] transition-opacity hover:opacity-85"
              style={{
                borderTop: "1px solid rgba(234,228,243,0.22)",
                color: "#eae4f3",
              }}
            >
              <span>написать в telegram</span>
              <span style={{ color: "#c23228" }}>→</span>
            </button>

            {/* Secondary — куратор подберёт место для встречи */}
            <button
              type="button"
              onClick={() => {
                navigate(`/places/${m.id}`);
              }}
              className="mt-[10px] flex w-full items-baseline justify-between pt-[12px] font-display italic font-light text-[15px] tracking-[-0.005em] transition-opacity hover:opacity-80"
              style={{
                borderTop: "1px solid rgba(234,228,243,0.10)",
                color: "rgba(234,228,243,0.65)",
              }}
            >
              <span>куратор подберёт место</span>
              <span style={{ color: "rgba(200,168,255,0.8)" }}>→</span>
            </button>

            {/* Secondary — editorial micro-caption */}
            <div className="mt-[18px] flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  navigate("/home");
                }}
                className="font-sans text-[11px] font-semibold uppercase transition-opacity hover:opacity-80"
                style={{
                  letterSpacing: "0.34em",
                  color: "rgba(234,228,243,0.6)",
                }}
              >
                Позже
              </button>
              <button
                type="button"
                onClick={() => {
                  // Safety entry — тихая ссылка, не крик. Backend готов (trpc.safety.reportCreate),
                  // но MATCHES — демо-данные с id="04", реального UUID нет. Когда Match перейдёт
                  // на live-матчи, поменять alert на вызов trpc и show toast.
                  const reason = window.prompt(
                    "Что не так? Опишите в двух словах — куратор посмотрит.",
                    ""
                  );
                  if (reason && reason.trim().length >= 3) {
                    console.info("safety:reported", { matchId: m.id, reason });
                    navigate("/home");
                  }
                }}
                className="font-sans text-[10px] italic transition-opacity hover:opacity-80"
                style={{
                  color: "rgba(234,228,243,0.4)",
                }}
              >
                что-то не так?
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
